"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "AI-Chatbots & Automatisering",
  description: "Stel je voor: vragen beantwoord, leads gekwalificeerd en afspraken ingepland. Terwijl jij slaapt, op vakantie bent of gewoon lekker bezig bent met je klanten. Geen extra handen nodig, geen eindeloze to-do lijst. Gewoon slimme AI die het werk doet dat jouw tijd opslokt, zodat jij je energie stopt in wat groei brengt.",
  bullets: ["Geen herhalende taken meer, focus op jouw groei", "24/7 bereikbaar, geen gemiste leads, geen gemiste kansen", "Gebouwd op jouw merk en jouw tone of voice"],
  link: "/ServicesPage",
  image: "https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/byjasminfotografie-192-scaled.jpg?v=xzsywgq73f"
}, {
  title: "Online Adverteren & Socials",
  description: "Jouw ideale klant scrolt nu door zijn feed. Wij zorgen dat jij degene bent die hij ziet. Met data-gedreven campagnes op Google en Meta die de juiste mensen bereiken op het juiste moment",
  bullets: ["Jouw budget ingezet waar het maximaal rendeert", "Consistent zichtbaar waar jouw klant scrolt", "Strategie die groeit met jouw onderneming"],
  link: "/ServicesPage",
  image: "https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/byjasminfotografie-260-scaled.jpg?v=ufxbgerq2qj"
}, {
  title: "AI-gedreven Websites",
  description: "De meeste websites zijn online visitekaartjes die stof verzamelen. Ziet er goed uit, doet verder niets. Wij geloven dat een website je hardst werkende medewerker moet zijn. 24/7 aan het werk, nooit op vakantie en bereikt iedereen tegelijk. Alleen resultaat.",
  bullets: ["Gebouwd om te converteren, niet alleen om er goed uit te zien", "Razendsnel, want een trage website is een lege kassa", "Volledig schaalbaar en mobiel-vriendelijk"],
  link: "/ServicesPage",
  image: "https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/byjasminfotografie-246-scaled.jpg?v=pkdlp17lxie"
}];
function ServiceCard({
  service,
  index
}: {
  service: (typeof services)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="118399" ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center bg-card/40 border border-border/50 backdrop-blur-md rounded-[2rem] p-6 md:p-10 lg:p-16 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      <div className={`relative aspect-square lg:aspect-[4/3] rounded-2xl overflow-hidden bg-muted/20 ${!isEven ? "lg:order-last" : ""}`}>
        <img src={service.image} alt={service.title} className="object-cover w-full h-full transition-transform duration-700 !opacity-[1] hover:scale-105" />
      </div>
      
      <div className="flex flex-col justify-center space-y-8">
        <div className="space-y-4">
          <h3 className="text-3xl md:text-4xl font-default font-bold tracking-tight text-primary">
            {service.title}
          </h3>
          <p className="text-lg font-default text-muted-foreground leading-relaxed">
            {service.description}
          </p>
        </div>
        
        <ul className="space-y-4">
          {service.bullets.map((bullet, i) => <li key={i} data-index={i} className="flex items-start gap-3">
              <CircleCheck className="size-6 text-accent-foreground shrink-0 mt-0.5" />
              <span className="text-base md:text-lg font-default text-foreground/90">{bullet}</span>
            </li>)}
        </ul>
        
        <div className="pt-2">
          <Button asChild size="lg" className="w-full sm:w-fit text-base h-12 px-8 font-default bg-primary text-primary-foreground hover:opacity-90">
            <Link to="/diensten">Bekijk dienst</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function OnzeGroeiformule() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="slimme-oplossingen" className="text-foreground py-24 overflow-hidden relative !bg-[hsl(20,100%,98%)] lg:py-32">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default tracking-tight mb-6 text-primary !text-5xl !font-semibold">Bekijk onze groeipijlers.</h2>
          
          
        </div>

        <div className="space-y-12 lg:space-y-24 max-w-7xl mx-auto">
          {services.map((service, index) => <ServiceCard key={index} service={service} index={index} />)}
        </div>
      </div>
    </section>;
}